/**
 * Map of original-file sizes for an event.
 *
 * The gallery lists the thumb_uploads/ prefix, so MediaItem.size is the
 * THUMBNAIL's size (~tens of KB). To show the real file size, we list the
 * uploads/ (originals) prefix once and key the byte sizes by original key.
 * Sidecar .json entries are ignored.
 */
import { useQuery } from '@tanstack/react-query';
import { listAllFiles } from '../lib/apiClient.ts';
import { eventPrefix, isSidecarKey } from '../lib/keys.ts';
import { UNCATEGORIZED_EVENT } from '../config/awsConfig.ts';

export function useOriginalSizes(event: string) {
  return useQuery({
    queryKey: ['original-sizes', event],
    staleTime: 60_000,
    queryFn: async ({ signal }): Promise<Map<string, number>> => {
      const isUncategorized = event === UNCATEGORIZED_EVENT;
      const files = await listAllFiles({
        prefix: eventPrefix(event, 'original'),
        maxKeys: 1000,
        // See useFileList for the same fix: keep Uncategorized flat, don't
        // recurse into other event folders.
        delimiter: isUncategorized ? '/' : undefined,
        signal,
      });
      const sizes = new Map<string, number>();
      for (const f of files) {
        if (isSidecarKey(f.key)) continue;
        if (typeof f.size === 'number') sizes.set(f.key, f.size);
      }
      return sizes;
    },
  });
}
